import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Hospital, Plus, Trash2, Edit2, Save, X, Mail, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
    { value: "admision", label: "Admisión / Urgencias" },
    { value: "urgencias", label: "Jefe de urgencias" },
    { value: "conmutador", label: "Conmutador" },
    { value: "jefe_turno", label: "Jefe de turno" },
    { value: "trauma", label: "Equipo de trauma" },
    { value: "uci", label: "UCI / Crítico" },
    { value: "otro", label: "Otro" },
];

const EMPTY = { hospital_name: "", name: "", role: "admision", email: "", phone: "", whatsapp: "" };

export default function HospitalContacts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // id or 'new'
    const [form, setForm] = useState(EMPTY);
    const [filter, setFilter] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/hospital-contacts");
            setItems(data || []);
        } catch (e) { console.warn(e); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const startNew = () => { setEditing("new"); setForm(EMPTY); };
    const startEdit = (c) => { setEditing(c.id); setForm(c); };
    const cancel = () => { setEditing(null); setForm(EMPTY); };

    const save = async (e) => {
        e.preventDefault();
        if (!form.hospital_name || !form.name) {
            toast.error("Hospital y nombre del contacto son requeridos");
            return;
        }
        try {
            if (editing === "new") {
                await api.post("/hospital-contacts", form);
                toast.success("Contacto agregado");
            } else {
                await api.put(`/hospital-contacts/${editing}`, form);
                toast.success("Contacto actualizado");
            }
            cancel();
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const remove = async (c) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm(`¿Eliminar contacto ${c.name} de ${c.hospital_name}?`)) return;
        try {
            await api.delete(`/hospital-contacts/${c.id}`);
            toast.success("Contacto eliminado");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const filtered = items.filter((c) => {
        if (!filter) return true;
        const t = filter.toLowerCase();
        return c.hospital_name?.toLowerCase().includes(t) || c.name?.toLowerCase().includes(t);
    });

    // Group by hospital
    const grouped = {};
    filtered.forEach((c) => {
        const k = c.hospital_name || "—";
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(c);
    });

    return (
        <div className="space-y-4" data-testid="hospital-contacts-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
                        <Hospital className="w-6 h-6 text-red-600" /> Contactos de Hospitales
                    </h2>
                    <div className="text-xs text-slate-500 font-mono">
                        Personas a notificar para pre-aviso de arribo de paciente · {items.length} contacto(s)
                    </div>
                </div>
                <button onClick={startNew} className="fm-btn-accent" data-testid="new-contact-btn">
                    <Plus className="w-3.5 h-3.5" /> Nuevo contacto
                </button>
            </div>

            <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Buscar hospital o nombre..."
                className="fm-input max-w-md"
                data-testid="search-contact-input"
            />

            {/* Form */}
            {editing && (
                <form onSubmit={save} className="fm-card p-5" data-testid="contact-form">
                    <h3 className="font-display font-bold text-base mb-3">
                        {editing === "new" ? "Nuevo contacto" : "Editar contacto"}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="md:col-span-2">
                            <label className="fm-label">Hospital *</label>
                            <input value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} className="fm-input" data-testid="form-hospital-name" />
                        </div>
                        <div>
                            <label className="fm-label">Rol</label>
                            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="fm-input" data-testid="form-role">
                                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-3">
                            <label className="fm-label">Nombre del contacto *</label>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fm-input" data-testid="form-name" />
                        </div>
                        <div>
                            <label className="fm-label">Email</label>
                            <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fm-input" data-testid="form-email" />
                        </div>
                        <div>
                            <label className="fm-label">Teléfono (SMS)</label>
                            <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+52..." className="fm-input" data-testid="form-phone" />
                        </div>
                        <div>
                            <label className="fm-label">WhatsApp</label>
                            <input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+52..." className="fm-input" data-testid="form-whatsapp" />
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button type="button" onClick={cancel} className="fm-btn-outline"><X className="w-3.5 h-3.5" /> Cancelar</button>
                        <button type="submit" className="fm-btn-accent" data-testid="save-contact-btn"><Save className="w-3.5 h-3.5" /> Guardar</button>
                    </div>
                </form>
            )}

            {/* List grouped */}
            {Object.keys(grouped).length === 0 ? (
                <div className="fm-card p-10 text-center text-sm text-slate-500">
                    <Hospital className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                    Sin contactos registrados todavía.
                </div>
            ) : (
                Object.entries(grouped).map(([hospital, contacts]) => (
                    <div key={hospital} className="fm-card overflow-hidden" data-testid={`hospital-group-${hospital}`}>
                        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                            <h3 className="font-display font-bold text-base flex items-center gap-2">
                                <Hospital className="w-4 h-4 text-red-600" /> {hospital}
                            </h3>
                            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">{contacts.length} contacto(s)</span>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-white border-b border-slate-200">
                                    {["Rol", "Nombre", "Email", "Teléfono", "WhatsApp", ""].map((h) => (
                                        <th key={h} className="text-left py-2 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map((c) => (
                                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                                        <td className="py-2 px-3 text-xs uppercase font-bold tracking-wider text-slate-600">{c.role}</td>
                                        <td className="py-2 px-3 font-medium">{c.name}</td>
                                        <td className="py-2 px-3 text-xs">{c.email ? <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span> : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-2 px-3 text-xs font-mono">{c.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span> : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-2 px-3 text-xs font-mono">{c.whatsapp ? <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {c.whatsapp}</span> : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-2 px-3">
                                            <div className="flex items-center gap-1 justify-end">
                                                <button onClick={() => startEdit(c)} className="p-1.5 hover:bg-slate-200 rounded-sm" data-testid={`edit-${c.id}`} aria-label="Editar">
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => remove(c)} className="p-1.5 hover:bg-red-100 text-red-600 rounded-sm" data-testid={`delete-${c.id}`} aria-label="Eliminar">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))
            )}
        </div>
    );
}
